import React, { PureComponent } from 'react';
import { Link, withRouter } from 'react-router-dom';
import ProposalRequest from '../views/proposal-requests/proposalRequest';
import SystemToolbar from '../components/nav/systemToolbar';
import LoadingWizard from '../components/spinner/wizard';
import RequestStatusControl from '../sections/proposal-request/components/RequestStatusControl';
import API from '../utils/API';
import moment from 'moment';
import '../assets/css/proposalRequest.scss';

/**
 * Section definitions — mirror the 9 wizard steps so the post-submission
 * experience is a direct 1:1 with the wizard the planner just used.
 * `complete` returns true when that section has meaningful data.
 */
const SECTIONS = [
	{
		id: 'event',
		label: 'Event Details',
		complete: (r) => !!(r.eventDetails?.eventName && r.eventDetails?.eventType && r.eventDetails?.arrivalDate)
	},
	{
		id: 'rooms',
		label: 'Room Block',
		complete: (r) => (r.roomBlock?.dailyGrid || []).length > 0
	},
	{
		id: 'agenda',
		label: 'Meeting Space',
		complete: (r) => (r.agendaItems || []).length > 0
	},
	{
		id: 'concessions',
		label: 'Concessions',
		complete: (r) => ((r.concessions?.custom) || []).length > 0
	},
	{
		id: 'considerations',
		label: 'Key Considerations',
		complete: (r) => !!(r.keyConsiderations?.decisionFactors || r.keyConsiderations?.keyConsiderations || r.keyConsiderations?.eventHistory)
	},
	{
		id: 'services',
		label: 'Additional Services',
		complete: (r) => (r.additionalServices || []).length > 0
	},
	{
		id: 'additional',
		label: 'Additional Info',
		complete: (r) => !!(r.additionalInfo?.responseDueDate || r.additionalInfo?.decisionDate || r.additionalInfo?.eventDescription)
	},
	{
		id: 'planner',
		label: 'Contract Signee',
		complete: (r) => !!(r.plannerInfo?.email || r.plannerInfo?.contactName)
	}/* ,
	{
		id: 'agency',
		label: 'Agency',
		complete: (r) => !!(r.agencyInfo?.agencyName || r.agencyInfo?.iataNumber)
	} */
];

class ProposalRequestLevelLayout extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			orgId: this.props.match.params.organization,
			requestId: this.props.match.params.request,
			request: {},
			venues: [],
			loading: true
		};
	}

	async componentDidMount() {
		await this.loadRequest();
	}

	loadRequest = async () => {
		const { requestId, orgId } = this.state;
		try {
			const request = await API().get(`Organizations/${orgId}/proposal-requests/${requestId}`);
			this.setState({
				loading: false,
				request: request.data,
				venues: request.data.proposalVenues || []
			});
		} catch (e) {
			console.log(e);
			this.setState({ loading: false });
		}
	};

	refreshSectionCounts = async () => {
		await this.loadRequest();
	};

	handleStatusUpdated = (nextStatus) => {
		this.setState({ request: { ...this.state.request, status: nextStatus } });
	};

	isActiveSection = (id) => {
		const { location } = this.props;
		const path = (location?.pathname || '').toLowerCase();
		if (id === 'venues') {
			return path.includes('/venues');
		}
		return path.includes(`/request/${id}`) || path.endsWith(`/${id}`);
	};

	render() {
		const { organizations, permission, organization, socket } = this.props;
		const { orgId, requestId, loading, request, venues } = this.state;

		const arrivalDate = request.eventDetails?.arrivalDate
			? moment(request.eventDetails.arrivalDate).format('MMM D')
			: null;
		const departureDate = request.eventDetails?.departureDate
			? moment(request.eventDetails.departureDate).format('MMM D, YYYY')
			: null;
		const dateRange = arrivalDate && departureDate ? `${arrivalDate} – ${departureDate}` : null;

		const venueCount = venues.length;
		const contractedCount = venues.filter(v => v.status === 'Contracted').length;

		const filled = SECTIONS.filter(s => s.complete(request || {})).length;

		return (
			<div className="application">
				<div className="topLevelPages">
					<div className="systemContentContainer">
						<SystemToolbar
							socket={socket}
							markNotificationAsRead={this.props.markNotificationAsRead}
							platformNotifications={this.props.platformNotifications}
							permission={this.props.permission}
							account={this.props.account}
							organization={organization}
							organizations={organizations}
							logout={this.props.logout}
							programsAvailable={this.props.programsAvailable}
							subscriptionAddons={this.props.subscriptionAddons}
							subscriptionType={this.props.subscriptionType}
							displayLogo={true}
							taskCount={this.props.taskCount}
							updateTaskCount={this.props.updateTaskCount}
							pingServer={this.props.pingServer}
						/>

						{loading && (
							<div style={{ height: 'calc(100% - 55px)' }}>
								<LoadingWizard text="LOADING PROPOSAL REQUEST" loading={loading} />
							</div>
						)}

						{!loading && (
							<div className="pr-root" style={{ height: 'calc(100% - 55px)' }}>
								{/* Dark wizard-style top bar */}
								<div className="pr-topbar">
									<div className="pr-topbar-left">
										<Link to={`/${orgId}/proposal-requests`} className="pr-topbar-exit">
											<i className="las la-arrow-left" />
											All Requests
										</Link>
									</div>

									<div className="pr-topbar-title">
										<i className="las la-file-alt" />
										{request.name || 'Proposal Request'}
									</div>

									<div className="pr-topbar-right">
										<div className="pr-status-control">
											<RequestStatusControl
												orgId={orgId}
												requestId={requestId}
												status={request.status || 'Draft'}
												onUpdated={this.handleStatusUpdated}
											/>
										</div>
										{dateRange && (
											<span className="pr-topbar-dates">
												<i className="las la-calendar mr-5" />
												{dateRange}
											</span>
										)}
									</div>
								</div>

								{/* Body: sidebar + content */}
								<div className="pr-body">
									<nav className="pr-sidebar">
								{/* Primary: the actual work */}
								<div className="pr-sidebar-group">
									<Link
										to={`/${orgId}/proposal-requests/${requestId}/request/review`}
										className={`pr-sidebar-link ${this.isActiveSection('review') ? 'active' : ''}`}
									>
										<span>Overview</span>
									</Link>
									<Link
										to={`/${orgId}/proposal-requests/${requestId}/venues`}
										className={`pr-sidebar-link ${this.isActiveSection('venues') ? 'active' : ''}`}
									>
										<span>Venues</span>
										<span className="pr-sidebar-badge">
											{contractedCount > 0
												? `${contractedCount}/${venueCount} contracted`
												: venueCount
											}
										</span>
									</Link>
								</div>

										{/* Secondary: the submitted request details (editable) */}
										<div className="pr-sidebar-group">
											<div className="pr-sidebar-group-label">
												Request · {filled}/{SECTIONS.length}
											</div>
											{SECTIONS.map(section => (
												<Link
													key={section.id}
													to={`/${orgId}/proposal-requests/${requestId}/request/${section.id}`}
													className={`pr-sidebar-link ${this.isActiveSection(section.id) ? 'active' : ''}`}
												>
													<span>{section.label}</span>
													<span
														className={`pr-sidebar-dot ${section.complete(request || {}) ? 'complete' : ''}`}
														title={section.complete(request || {}) ? 'Provided' : 'Not provided yet'}
													/>
												</Link>
											))}
										</div>
									</nav>

									<ProposalRequest
										{...this.props}
										request={request}
										venues={venues}
										refreshSectionCounts={this.refreshSectionCounts}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

export { SECTIONS };
export default withRouter(ProposalRequestLevelLayout);
